import React, { useContext, useMemo, useState } from "react";
import styled from "styled-components";
import { colors2, icons } from "oolib";
import { Lightbox, LightboxTrigger, LightboxContent } from "@/components/UI/lightbox";
import { ListItem } from "@/components/UI/List/comps/ListItem";
import { DrilldownDepthContext } from "@/components/UI/DrilldownList/context/DrilldownDepthContext";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";
import { FilterOptionsPanel } from "./FilterOptionsPanel";
import { getFilterIcon } from "../utils/getFilterIcon";
import { hasFilterValue } from "../../../utils/filterConstants";

const { CaretRight } = icons;
const { grey10, grey40 } = colors2;

export const LIGHTBOX_Z_INDEX_BASE = 100000;

export const StyledScrollableWrapper = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: column;
  ${globalScrollbarStyling}
`;

export const StyledHierarchyItem = styled.div`
  position: relative;

  /* LightboxTrigger renders as inline-flex span — stretch it full width */
  > span {
    width: 100%;
  }
`;

export const StyledConnector = styled.div`
  position: absolute;
  top: -7px;
  left: ${({ $level }) => `${$level * 2 - 0.4}rem`};
  width: 0.9rem;
  height: 2.6rem;
  border-left: 1.5px solid #C8C8C8;
  border-bottom: 1.5px solid #C8C8C8;
  border-bottom-left-radius: 6px;
  z-index: 1;
`;

const OVERFLOW_VISIBLE = { overflow: "visible" };

/** Marks a change as originating in the Add Filter picker. See FilterListItem. */
export const PICKER_ORIGIN = { fromPicker: true };


/** Row wrapper + chevron "handle" for the selectable-folder variant, so
 *  "select the folder" (body) and "drill in" (chevron) stay distinct click targets. */
const StyledSelectableRow = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
`;

const StyledDrillHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: none;
  padding: 0 1rem;
  border-radius: 0.4rem;
  &:hover {
    background-color: ${grey10};
  }
`;

/**
 * Universal depth-aware panel primitive.
 * Renders a ListItem inside a Lightbox that opens a sub-panel with arbitrary children.
 *
 * Used directly as a folder (DocHierarchyFilter sections/datasets)
 * and composed by FilterListItem (filter entries with options panel).
 *
 * `onSelect` (optional): makes the row BODY selectable. When passed, clicking the
 * ListItem body emits `onSelect()` (single-select) and ONLY the chevron handle
 * drills into `children` — the two are mutually exclusive per click. Absent →
 * today's behaviour (the whole row is the drill trigger). Used by the reports
 * cascade picker's Thematics/Attributes folders.
 */
export const PanelItem = React.memo(({ option, children, zIndex: zIndexProp, style, contentStyle, isSelected, onSelect }) => {
  const parentDepth = useContext(DrilldownDepthContext);
  const zIndex = zIndexProp ?? (LIGHTBOX_Z_INDEX_BASE + parentDepth + 1);
  const [isOpen, setIsOpen] = useState(false);

  const lightboxStyle = contentStyle ? { zIndex, ...contentStyle } : { zIndex };
  const forceCascade = contentStyle?.overflow === "visible";
  const selectable = typeof onSelect === "function";

  return (
    <DrilldownDepthContext.Provider value={parentDepth + 1}>
      <Lightbox placement="right-start" onOpenChange={setIsOpen} multilayer={forceCascade ? false : undefined} enabled={!(option.disabled || option.disabledReason)}>
        {selectable ? (
          // Body selects the whole folder; only the chevron handle drills.
          <StyledSelectableRow>
            <div style={{ flex: 1, minWidth: 0 }}>
              <ListItem
                option={option}
                isMulti={false}
                S={true}
                hasSubmenu={false}
                isSelected={isSelected}
                onChange={() => onSelect()}
                style={{ width: "100%", ...style }}
              />
            </div>
            <LightboxTrigger>
              <StyledDrillHandle
                role="button"
                tabIndex={0}
                aria-label="Show sub-options"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <CaretRight size={16} color={grey40} />
              </StyledDrillHandle>
            </LightboxTrigger>
          </StyledSelectableRow>
        ) : (
          <LightboxTrigger>
            {/* isSelected (optional): marks a folder that CONTAINS the current
                selection (path indicator). Default undefined → filter unaffected. */}
            <ListItem option={option} isMulti={false} S={true} hasSubmenu isSelected={isSelected} style={{ width: "100%", ...style }} />
          </LightboxTrigger>
        )}
        <LightboxContent cascade style={lightboxStyle}>
          {isOpen && children}
        </LightboxContent>
      </Lightbox>
    </DrilldownDepthContext.Provider>
  );
});

/**
 * Filter entry that opens a lightbox with FilterOptionsPanel.
 * Composes PanelItem with filter-specific option building and data fetching.
 *
 * Used by DocHierarchyFilter, AnnoHierarchyFilter (common filters),
 * and anywhere a single filter needs a lightbox-based options panel.
 *
 * Changes emitted from here are stamped `{ fromPicker: true }` — they originate in
 * the Add Filter cascade rather than an applied-filter block. handleOnChange uses
 * that to keep multi-path fan-out live for the whole panel session, so checking
 * several boxes on one field applies to every direction, not just the first check.
 *
 * @param {Object}  filter          - Filter config with filterId, display, source, etc.
 * @param {Object}  selectedValues  - Map of filterId → selected values array
 * @param {Function} onChange       - (filterId, newValues, meta) => void
 * @param {string}  [icon]          - Override icon name (defaults to getFilterIcon)
 * @param {number}  [zIndex]        - Override zIndex (defaults to context-based computation)
 * @param {Array}   [contentTypes]  - Content types for option fetching
 * @param {Array}   [activeFilters] - Active filters for option fetching
 */
export const FilterListItem = React.memo(({
  filter,
  selectedValues,
  onChange,
  zIndex,
  contentTypes,
  activeFilters,
  icon,
  style,
  // Optional immediate child filter (e.g. State for Country) — enables value
  // drilldown in the option list, committing to the child's own filterId.
  childFilterConfig,
}) => {
  const currentValues = selectedValues[filter.filterId];
  const hasValues = hasFilterValue(currentValues);

  const option = useMemo(() => ({
    _id: filter.filterId,
    display: filter.display,
    ...(icon ? { icon } : getFilterIcon(filter)),
    desc: filter.isSharedFilter ? "Shared Filter" : undefined,
    hasChildren: true,
    disabledReason: filter.disabledReason,
  }), [filter.filterId, filter.display, filter.isSharedFilter, icon, filter]);

  const needsOverflow = filter?.source?.filterType === "dateRangeType";

  return (
    <PanelItem option={option} zIndex={zIndex} style={{...style}} contentStyle={needsOverflow ? { overflow: "visible" } : {}}>
      <FilterOptionsPanel
        filterConfig={filter}
        value={currentValues ?? []}
        onChange={(newValues) => onChange(filter.filterId, newValues, PICKER_ORIGIN)}
        contentTypes={contentTypes}
        activeFilters={activeFilters}
        childFilterConfig={childFilterConfig}
        childValue={childFilterConfig ? (selectedValues[childFilterConfig.filterId] ?? []) : []}
        onChildChange={childFilterConfig ? (vals) => onChange(childFilterConfig.filterId, vals, PICKER_ORIGIN) : undefined}
      />
    </PanelItem>
  );
});
